<template>
  <el-form
    ref="formRef"
    :model="localValue"
    :rules="formRules"
    :disabled="disabled"
    label-width="120px"
    class="campaign-form"
  >
    <!-- 活动名称 -->
    <el-form-item label="活动名称" prop="campaignName">
      <el-input
        v-model="localValue.campaignName"
        :maxlength="20"
        show-word-limit
        :disabled="fieldDisabled('campaignName')"
        placeholder="请输入活动名称"
        clearable
      />
    </el-form-item>

    <!-- 活动进行时间 -->
    <el-form-item label="活动进行时间" prop="dateRange">
      <el-date-picker
        v-model="localValue.dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        :disabled="fieldDisabled('dateRange')"
        :picker-options="datePickerOptions"
      />
    </el-form-item>

    <!-- 前置免租 -->
    <el-form-item label="前置免租" prop="preFreeRentDays">
      <el-input
        :value="String(localValue.preFreeRentDays)"
        maxlength="3"
        :disabled="fieldDisabled('preFreeRentDays')"
        placeholder="请输入0~365之间的数字"
        style="width: 185px;"
        @input="handlePreFreeRentDaysInput"
      >
        <template slot="append">天</template>
      </el-input>
    </el-form-item>

    <!-- 满租赠送 -->
    <el-form-item label="满租赠送" prop="fullRentGiftDays">
      <el-input
        :value="String(localValue.fullRentGiftDays)"
        maxlength="3"
        :disabled="fieldDisabled('fullRentGiftDays')"
        placeholder="请输入0~365之间的数字"
        style="width: 185px;"
        @input="handleFullRentGiftDaysInput"
      >
        <template slot="append">天</template>
      </el-input>
    </el-form-item>

    <!-- 关联方案 -->
    <el-form-item v-if="!hideBindPlan" label="关联方案" prop="bindPlanIds">
      <el-select
        ref="planSelect"
        v-model="localValue.bindPlanIds"
        multiple
        collapse-tags
        :disabled="fieldDisabled('bindPlanIds')"
        placeholder="请选择关联方案"
        @click.native.prevent="openPlanDialog"
        @visible-change="blockPlanSelectDropdown"
      >
        <el-option
          v-for="item in planList"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>
    </el-form-item>

    <!-- 方案选择弹窗 -->
    <el-dialog
      :visible.sync="planDialogVisible"
      title="选择关联方案"
      :close-on-click-modal="false"
      :append-to-body="true"
      class="plan-transfer-dialog"
    >
      <el-transfer
        v-model="planDialogSelectedIds"
        :data="planList"
        :props="{ key: 'id', label: 'name' }"
        :titles="['未选择方案', '已选择方案']"
        filterable
      />
      <div slot="footer" class="dialog-footer">
        <el-button @click="planDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPlanSelection">确定</el-button>
      </div>
    </el-dialog>
  </el-form>
</template>

<script>
import { getLeaseSchemeListSimple } from '@/api/domain/leaseScheme'
import { getMoneyValue } from '@/framework/directive/index.js'
import { CAMPAIGN_STATUS } from '@/pages/activity/leaseCampaign/leaseCampaignConst'

export default {
  name: 'CampaignForm',

  props: {
    /** v-model: campaign data object */
    value: {
      type: Object,
      default: () => ({}),
    },
    /** Whether fields are disabled (view mode) */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Form mode: 'create' | 'edit' | 'view' */
    mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'edit', 'view'].includes(v),
    },
    /** Current campaign ID (for edit/view, used in conflict check excludeCampaignId) */
    campaignId: {
      type: Number,
      default: null,
    },
    /** Current campaign status (for IN_PROGRESS time fields readonly) */
    campaignStatus: {
      type: Number,
      default: null,
    },
    /**
     * Whether to hide the bindPlanIds field (used when creating from plan context)
     */
    hideBindPlan: {
      type: Boolean,
      default: false,
    },
    /** Initial plan options from detail API, for quick display before full list loads */
    initialPlans: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localValue: this.getDefaultFormData(),
      planList: [],
      fullListLoaded: false,
      planDialogVisible: false,
      planDialogSelectedIds: [],
    }
  },

  computed: {
    formRules() {
      const rules = {
        campaignName: [
          { required: true, message: '请输入活动名称', trigger: 'blur' },
        ],
        dateRange: [
          { required: true, message: '请选择活动进行时间', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              if (!value || value.length < 2) {
                callback()
                return
              }
              const [start, end] = value
              if (!start || !end) {
                callback()
                return
              }
              if (new Date(start).getTime() > new Date(end).getTime()) {
                callback(new Error('开始日期不能晚于结束日期'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          },
        ],
        preFreeRentDays: [
          { required: true, message: '请输入前置免租天数', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value < 0 || value > 365) {
                callback(new Error('请输入0~365之间的数字'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        fullRentGiftDays: [
          { required: true, message: '请输入满租赠送天数', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value < 0 || value > 365) {
                callback(new Error('请输入0~365之间的数字'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
      }
      if (!this.hideBindPlan) {
        rules.bindPlanIds = [
          {
            type: 'array',
            required: true,
            message: '请关联至少一个方案',
            trigger: 'change',
          },
        ]
      }
      return rules
    },

    datePickerOptions() {
      if (this.mode !== 'view') {
        return {
          disabledDate: (time) => {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return time.getTime() < today.getTime()
          },
        }
      }
      return {}
    },
  },

  watch: {
    value: {
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          const merged = { ...this.getDefaultFormData(), ...val }
          if (JSON.stringify(merged) !== JSON.stringify(this.localValue)) {
            this.localValue = merged
          }
        }
      },
      immediate: true,
      deep: true,
    },
    localValue: {
      handler(val) {
        this.$emit('input', { ...val })
      },
      deep: true,
    },
    initialPlans: {
      handler(val) {
        if (val && val.length > 0 && !this.fullListLoaded) {
          this.planList = [...val]
        }
      },
      immediate: true,
    },
  },

  created() {
    if (this.mode !== 'view') {
      this.fetchPlanList()
    }
  },

  methods: {
    getDefaultFormData() {
      return {
        campaignName: '',
        dateRange: [],
        preFreeRentDays: '',
        fullRentGiftDays: '',
        bindPlanIds: [],
      }
    },

    handlePreFreeRentDaysInput(value) {
      const days = getMoneyValue(value) > 365 ? 365 : getMoneyValue(value)
      this.localValue.preFreeRentDays = days
    },

    handleFullRentGiftDaysInput(value) {
      const days = getMoneyValue(value) > 365 ? 365 : getMoneyValue(value)
      this.localValue.fullRentGiftDays = days
    },

    fieldDisabled(fieldName) {
      if (this.mode === 'view') return true
      if (this.disabled) return true
      // During edit mode with IN_PROGRESS status, time fields are readonly
      if (
        this.mode === 'edit' &&
        this.campaignStatus === CAMPAIGN_STATUS.IN_PROGRESS &&
        fieldName === 'dateRange'
      ) {
        return true
      }
      return false
    },


    async fetchPlanList() {
      const { code, data } = await getLeaseSchemeListSimple({
        pageNum: 1,
        pageSize: 9999,
      })
      if (code === 1 && data?.items) {
        this.planList = data.items
          .filter((item) => item.id != null)
          .map((item) => ({
            id: item.id,
            name: item.name,
            state: item.state,
          }))
        this.fullListLoaded = true
      }
    },

    openPlanDialog() {
      if (this.fieldDisabled('bindPlanIds')) return
      this.planDialogSelectedIds = [...this.localValue.bindPlanIds]
      this.planDialogVisible = true
    },

    blockPlanSelectDropdown(visible) {
      if (visible) {
        this.$nextTick(() => {
          if (this.$refs.planSelect) this.$refs.planSelect.visible = false
        })
      }
    },

    confirmPlanSelection() {
      this.localValue.bindPlanIds = [...this.planDialogSelectedIds]
      this.planDialogVisible = false
    },

    /**
     * Validate the form. Returns a Promise.
     * Resolves on success, rejects with validation errors.
     */
    // eslint-disable-next-line vue/no-unused-properties
    validate() {
      return this.$refs.formRef.validate()
    },

    /**
     * Reset form fields to defaults and clear validation.
     */
    // eslint-disable-next-line vue/no-unused-properties
    resetFields() {
      this.localValue = this.getDefaultFormData()
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate()
      })
    },

    /**
     * Build the backend save params from frontend form data.
     * @returns {Object} Backend-ready params
     */
    // eslint-disable-next-line vue/no-unused-properties
    buildSaveParams() {
      const [startDate = '', endDate = ''] = this.localValue.dateRange || []
      return {
        id: this.campaignId || undefined,
        activityName: this.localValue.campaignName,
        startDate,
        endDate,
        preFreeRentDays: this.localValue.preFreeRentDays,
        presentDays: this.localValue.fullRentGiftDays,
        planIdList: this.localValue.bindPlanIds,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.campaign-form {
  padding: 16px 0;
}


.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}
</style>

<style lang="scss">
.plan-transfer-dialog {
  .el-dialog {
    width: fit-content;
    min-width: 520px;
  }
}
</style>
